import type {
  ArchiveDocumentInput,
  ArchivedDocument,
  DocumentArchiveGateway,
} from '@forq/contracts';

/**
 * Belegarchiv ohne Datenbank -- für den Demo-Betrieb. Bewusst NUR im Arbeitsspeicher: eine
 * Aufbewahrungspflicht lässt sich im Browser nicht erfüllen, und ein `localStorage`-Archiv würde
 * genau das vortäuschen. Die Schreib-Einmaligkeit bildet es aber ab, damit Demo und Produktivbetrieb
 * dieselbe Regel zeigen.
 */
export class DemoDocumentArchiveGateway implements DocumentArchiveGateway {
  private readonly entries = new Map<string, ArchivedDocument>();
  private counter = 0;

  private key(tenantId: string, documentNumber: string): string {
    return `${tenantId} ${documentNumber}`;
  }

  async archiveDocument(input: ArchiveDocumentInput): Promise<ArchivedDocument> {
    const key = this.key(input.tenantId, input.documentNumber);
    const existing = this.entries.get(key);
    // Schreib-einmalig: ein zweiter Versuch liefert den vorhandenen Eintrag, er überschreibt nicht.
    if (existing) return structuredClone(existing);

    this.counter += 1;
    const entry: ArchivedDocument = {
      id: `demo-archive-${this.counter}`,
      tenantId: input.tenantId,
      kind: input.kind,
      documentNumber: input.documentNumber,
      contentHtml: input.contentHtml,
      checksum: input.checksum,
      snapshot: input.snapshot,
      archivedAt: new Date().toISOString(),
      archivedBy: 'demo',
    };
    this.entries.set(key, entry);
    return structuredClone(entry);
  }

  async getArchivedDocument(tenantId: string, documentNumber: string): Promise<ArchivedDocument | null> {
    const entry = this.entries.get(this.key(tenantId, documentNumber));
    return entry ? structuredClone(entry) : null;
  }
}

export function createDemoDocumentArchiveGateway(): DocumentArchiveGateway {
  return new DemoDocumentArchiveGateway();
}
